
import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  FaGraduationCap,
  FaBookOpen,
  FaHeart,
  FaLightbulb,
  FaUsers,
  FaAward,
  FaBullseye,
  FaEye,
  FaCheckCircle,
  FaArrowRight,
  FaHandsHelping,
  FaGlobeAfrica,
  FaSchool,
  FaStar,
  FaQuoteLeft,
  FaSeedling,
} from "react-icons/fa";
import "./About.css";
import bgImage from "./janastudent.jpg";

const foundationValues = [
  {
    icon: FaGraduationCap,
    title: "Educational Excellence",
    description:
      "Promoting quality education, lifelong learning, and opportunities for learners to achieve their full potential.",
  },
  {
    icon: FaHeart,
    title: "Compassion",
    description:
      "Creating a culture of care, empathy, inclusion, and support for individuals and communities.",
  },
  {
    icon: FaLightbulb,
    title: "Innovation",
    description:
      "Encouraging curiosity, creative thinking, problem-solving, and new approaches to learning.",
  },
  {
    icon: FaUsers,
    title: "Community",
    description:
      "Building meaningful partnerships that strengthen families, empower communities, and support educational progress.",
  },
  {
    icon: FaAward,
    title: "Integrity",
    description:
      "Upholding honesty, accountability, transparency, and responsible stewardship in all our endeavors.",
  },
  {
    icon: FaSeedling,
    title: "Empowerment",
    description:
      "Helping people build the knowledge, confidence, and skills they need to shape a brighter future.",
  },
];

const foundationPriorities = [
  {
    number: "01",
    title: "Access to Education",
    description:
      "Supporting opportunities for individuals to learn, grow, and pursue their educational aspirations.",
  },
  {
    number: "02",
    title: "Personal Development",
    description:
      "Encouraging character, confidence, critical thinking, and the development of lifelong skills.",
  },
  {
    number: "03",
    title: "Community Impact",
    description:
      "Promoting collaboration and initiatives that contribute to stronger, more resilient communities.",
  },
];

const About = () => {
  useEffect(() => {
    AOS.init({
      duration: 850,
      once: true,
      offset: 70,
      easing: "ease-out-cubic",
    });

    AOS.refresh();
  }, []);

  return (
    <main className="jlw-about">
      {/* HERO */}
      <section
        className="jlw-hero"
        style={{ "--jlw-hero-image": `url("${bgImage}")` }}
      >
        <div className="jlw-hero-shade" />

        <div className="jlw-hero-inner">
          <div className="jlw-hero-content" data-aos="fade-up">
            <div className="jlw-eyebrow jlw-hero-eyebrow">
              <span className="jlw-eyebrow-line" />
              EDUCATION. OPPORTUNITY. IMPACT.
            </div>

            <h1>
              Every Learner
              <br />
              Deserves a <span>Brighter Future.</span>
            </h1>

            <p className="jlw-hero-description">
              At Jana L. Williams Educational Foundation, we believe
              education opens doors, strengthens communities, and
              creates opportunities for generations to come.
            </p>

            <div className="jlw-hero-actions">
              <a href="#jlw-story" className="jlw-btn jlw-btn-gold">
                Discover Our Foundation
                <FaArrowRight />
              </a>

              <a href="#jlw-purpose" className="jlw-btn jlw-btn-outline">
                What We Stand For
              </a>
            </div>

            <div className="jlw-hero-note">
              <FaCheckCircle />
              <span>Investing in people. Inspiring possibilities.</span>
            </div>
          </div>

          <div className="jlw-hero-side" data-aos="fade-left">
            <div className="jlw-hero-emblem">
              <FaGraduationCap />
            </div>

            <span className="jlw-hero-side-label">OUR COMMITMENT</span>

            <h2>
              Knowledge
              <br />
              Creates
              <br />
              <span>Possibility.</span>
            </h2>

            <div className="jlw-hero-side-rule" />

            <p>
              A future where learning inspires progress and
              opportunity reaches further.
            </p>
          </div>
        </div>

        <div className="jlw-hero-bottom">
          <span>JANА L. WILLIAMS EDUCATIONAL FOUNDATION</span>
          <a href="#jlw-story" aria-label="Scroll to our story">
            <span>SCROLL TO EXPLORE</span>
            <FaArrowRight />
          </a>
        </div>
      </section>

      {/* FOUNDATION INTRODUCTION */}
      <section className="jlw-story" id="jlw-story">
        <div className="jlw-container jlw-story-grid">
          <div className="jlw-story-visual" data-aos="fade-right">
            <div className="jlw-story-image-frame">
              <img
                src={bgImage}
                alt="Education and learning at Jana L. Williams Educational Foundation"
                loading="lazy"
              />

              <div className="jlw-image-shade" />

              <div className="jlw-image-caption">
                <span className="jlw-caption-icon">
                  <FaBookOpen />
                </span>

                <div>
                  <strong>Education with Purpose</strong>
                  <span>Building pathways to opportunity</span>
                </div>
              </div>
            </div>

            <div className="jlw-story-decoration" />
            <div className="jlw-story-index">
              <span>OUR</span>
              <strong>STORY</strong>
            </div>
          </div>

          <div className="jlw-story-content" data-aos="fade-left">
            <div className="jlw-eyebrow">
              <span className="jlw-eyebrow-line" />
              WHO WE ARE
            </div>

            <h2>
              Education That
              <br />
              Opens <span>New Doors.</span>
            </h2>

            <p className="jlw-story-lead">
              The Jana L. Williams Educational Foundation is
              presented here as an organization committed to
              advancing educational opportunity and helping
              individuals build a stronger future.
            </p>

            <p>
              We believe education is more than academic achievement.
              It is a foundation for personal growth, confidence,
              responsible leadership, and meaningful participation
              in society.
            </p>

            <p>
              Through a commitment to learning, empowerment, and
              community development, the Foundation seeks to
              encourage a future in which people have greater
              opportunities to learn, contribute, and thrive.
            </p>

            <div className="jlw-story-checks">
              <div>
                <FaCheckCircle />
                <span>Educational Opportunity</span>
              </div>

              <div>
                <FaCheckCircle />
                <span>Personal Development</span>
              </div>

              <div>
                <FaCheckCircle />
                <span>Community Empowerment</span>
              </div>

              <div>
                <FaCheckCircle />
                <span>Lifelong Learning</span>
              </div>
            </div>

            <a href="#jlw-purpose" className="jlw-text-link">
              Explore Our Purpose
              <FaArrowRight />
            </a>
          </div>
        </div>
      </section>

      {/* PURPOSE INTRO */}
      <section className="jlw-purpose" id="jlw-purpose">
        <div className="jlw-container">
          <div className="jlw-section-heading" data-aos="fade-up">
            <div className="jlw-eyebrow jlw-eyebrow-centered">
              <span className="jlw-eyebrow-line" />
              <span>OUR FOUNDATION</span>
              <span className="jlw-eyebrow-line" />
            </div>

            <h2>
              Guided by Purpose.
              <br />
              <span>Driven by Possibility.</span>
            </h2>

            <p>
              Our guiding principles reflect a commitment to
              education, human development, and the belief that
              opportunity can help transform lives.
            </p>
          </div>

          <div className="jlw-purpose-grid">
            <article
              className="jlw-purpose-card jlw-mission-card"
              data-aos="fade-up"
            >
              <div className="jlw-purpose-card-top">
                <span className="jlw-purpose-icon">
                  <FaBullseye />
                </span>
                <span className="jlw-card-number">01 / PURPOSE</span>
              </div>

              <span className="jlw-card-kicker">OUR MISSION</span>

              <h3>Creating Opportunities Through Education</h3>

              <p>
                To advance educational opportunity and personal
                development by encouraging access to learning,
                promoting empowerment, and supporting initiatives
                that help individuals and communities thrive.
              </p>

              <div className="jlw-card-bottom">
                <span>Opportunity for growth</span>
                <FaArrowRight />
              </div>
            </article>

            <article
              className="jlw-purpose-card jlw-vision-card"
              data-aos="fade-up"
              data-aos-delay="150"
            >
              <div className="jlw-purpose-card-top">
                <span className="jlw-purpose-icon">
                  <FaEye />
                </span>
                <span className="jlw-card-number">02 / FUTURE</span>
              </div>

              <span className="jlw-card-kicker">OUR VISION</span>

              <h3>A Future Where Learning Changes Lives</h3>

              <p>
                To contribute to a future in which educational
                opportunity, personal growth, and community
                empowerment help people reach their potential
                and make a positive difference in the world.
              </p>

              <div className="jlw-card-bottom">
                <span>Progress that lasts</span>
                <FaArrowRight />
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="jlw-values">
        <div className="jlw-container">
          <div className="jlw-values-heading" data-aos="fade-up">
            <div>
              <div className="jlw-eyebrow">
                <span className="jlw-eyebrow-line" />
                WHAT GUIDES US
              </div>

              <h2>
                The Values Behind
                <br />
                <span>Our Purpose.</span>
              </h2>
            </div>

            <p>
              Meaningful progress begins with strong principles.
              These values provide a foundation for the way we
              approach education, personal growth, and service.
            </p>
          </div>

          <div className="jlw-values-grid">
            {foundationValues.map((value, index) => {
              const ValueIcon = value.icon;

              return (
                <article
                  className="jlw-value-card"
                  key={value.title}
                  data-aos="fade-up"
                  data-aos-delay={(index % 3) * 100}
                >
                  <div className="jlw-value-top">
                    <span className="jlw-value-icon">
                      <ValueIcon />
                    </span>

                    <span className="jlw-value-number">
                      0{index + 1}
                    </span>
                  </div>

                  <h3>{value.title}</h3>

                  <p>{value.description}</p>

                  <span className="jlw-value-bottom-line" />
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* EDUCATIONAL APPROACH */}
      <section className="jlw-approach">
        <div className="jlw-container jlw-approach-grid">
          <div className="jlw-approach-content" data-aos="fade-right">
            <div className="jlw-eyebrow">
              <span className="jlw-eyebrow-line" />
              OUR APPROACH
            </div>

            <h2>
              From Learning
              <br />
              to <span>Lasting Impact.</span>
            </h2>

            <p className="jlw-approach-intro">
              Education becomes even more meaningful when
              knowledge is translated into confidence, skills,
              responsible action, and positive change.
            </p>

            <div className="jlw-priorities">
              {foundationPriorities.map((priority) => (
                <div className="jlw-priority" key={priority.number}>
                  <span className="jlw-priority-number">
                    {priority.number}
                  </span>

                  <div>
                    <h3>{priority.title}</h3>
                    <p>{priority.description}</p>
                  </div>

                  <FaArrowRight className="jlw-priority-arrow" />
                </div>
              ))}
            </div>
          </div>

          <div className="jlw-approach-visual" data-aos="fade-left">
            <div className="jlw-approach-image">
              <img
                src={bgImage}
                alt="Inspiring educational opportunity and community progress"
                loading="lazy"
              />

              <div className="jlw-approach-image-overlay" />
            </div>

            <div className="jlw-approach-message">
              <span className="jlw-approach-message-icon">
                <FaHandsHelping />
              </span>

              <span className="jlw-message-label">
                OUR BELIEF
              </span>

              <h3>
                When people have opportunities to learn, they can
                help build a better tomorrow.
              </h3>

              <div className="jlw-message-footer">
                <span />
                <FaGlobeAfrica />
                <span />
              </div>
            </div>

            <div className="jlw-approach-stamp">
              <FaStar />
              <span>LEARN</span>
              <span>GROW</span>
              <span>INSPIRE</span>
            </div>
          </div>
        </div>
      </section>

      {/* COMMITMENT STRIP */}
      <section className="jlw-commitment">
        <div className="jlw-container">
          <div className="jlw-commitment-heading" data-aos="fade-up">
            <span>WHAT WE ARE WORKING TOWARD</span>

            <h2>
              A Stronger Future Starts
              <br />
              <span>with Opportunity.</span>
            </h2>
          </div>

          <div className="jlw-commitment-grid">
            <div data-aos="fade-up">
              <span className="jlw-commitment-icon">
                <FaGraduationCap />
              </span>

              <h3>Learning</h3>

              <p>
                Encouraging educational development and a passion
                for lifelong learning.
              </p>
            </div>

            <div data-aos="fade-up" data-aos-delay="100">
              <span className="jlw-commitment-icon">
                <FaSchool />
              </span>

              <h3>Opportunity</h3>

              <p>
                Supporting pathways that help individuals pursue
                their goals and aspirations.
              </p>
            </div>

            <div data-aos="fade-up" data-aos-delay="200">
              <span className="jlw-commitment-icon">
                <FaHandsHelping />
              </span>

              <h3>Empowerment</h3>

              <p>
                Promoting confidence, practical development, and
                constructive participation in society.
              </p>
            </div>

            <div data-aos="fade-up" data-aos-delay="300">
              <span className="jlw-commitment-icon">
                <FaGlobeAfrica />
              </span>

              <h3>Community</h3>

              <p>
                Recognizing the importance of collaboration in
                creating lasting positive change.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="jlw-cta" id="jlw-contact">
        <div className="jlw-cta-pattern" />

        <div className="jlw-container jlw-cta-inner" data-aos="fade-up">
          <div className="jlw-cta-icon">
            <FaQuoteLeft />
          </div>

          <div className="jlw-cta-eyebrow">
            THE FUTURE BEGINS WITH OPPORTUNITY
          </div>

          <h2>
            Invest in Learning.
            <br />
            <span>Inspire a Lifetime.</span>
          </h2>

          <p>
            Every opportunity to learn can open the door to
            something greater. Join us in supporting a future
            shaped by education, empowerment, and possibility.
          </p>

          <div className="jlw-cta-actions">
            <a href="#jlw-story" className="jlw-btn jlw-btn-gold">
              Learn About Our Foundation
              <FaArrowRight />
            </a>

            <a href="#jlw-purpose" className="jlw-cta-text-link">
              Explore Our Mission
            </a>
          </div>

          <div className="jlw-cta-signature">
            <span />
            JANA L. WILLIAMS EDUCATIONAL FOUNDATION
            <span />
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
